import { useState, useEffect } from "react";
import { X, Star } from "lucide-react";

const PLAY_STORE_PACKAGE = "com.x67.app";
const DISMISS_KEY = "x67_smart_banner_dismissed_at";
const DISMISS_DAYS = 7;

/**
 * Smart App Banner — shows a slim Play Store install prompt at the top of the
 * page on Android Chrome, ONLY when the user is NOT already inside the TWA.
 *
 * UX rules:
 *  - Android Chrome + display-mode != standalone → show
 *  - Dismissed via X → hide for 7 days (localStorage)
 *  - Click "Instalează" → market://details?id=com.x67.app
 *    (opens Play Store app on Android; falls back to https URL)
 */
export default function SmartAppBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Skip on Server-Side Render / inside TWA / inside any PWA
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.matchMedia?.("(display-mode: fullscreen)").matches ||
      window.navigator.standalone === true; // iOS Safari
    if (isStandalone) return;

    // Only show on Android Chrome — iOS not supported yet
    const ua = window.navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    const isChrome = /Chrome/i.test(ua) && !/Edg|OPR|Opera|SamsungBrowser/i.test(ua);
    if (!isAndroid || !isChrome) return;

    // Honor previous dismissal for 7 days
    try {
      const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (at && Date.now() - at < DISMISS_DAYS * 24 * 3600 * 1000) return;
    } catch (_) {
      // localStorage unavailable — show banner anyway
    }

    setShow(true);
    // Push body down so the fixed banner doesn't overlap the top nav
    document.body.classList.add("has-smart-app-banner");
    return () => document.body.classList.remove("has-smart-app-banner");
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
    setShow(false);
    document.body.classList.remove("has-smart-app-banner");
  };

  const install = () => {
    // Prefer market:// (opens Play Store app directly), fall back to https
    const marketUrl = `market://details?id=${PLAY_STORE_PACKAGE}`;
    const httpsUrl = `https://play.google.com/store/apps/details?id=${PLAY_STORE_PACKAGE}`;
    // Try the deep link; if it fails, the catch redirects to the web Play Store.
    setTimeout(() => {
      // If user is still on our page after 1.5s the market app didn't open
      // (e.g. installed Chrome only). Send them to the web Play Store.
      window.location.href = httpsUrl;
    }, 1500);
    window.location.href = marketUrl;
  };

  if (!show) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[90] bg-gradient-to-r from-amber-500/95 via-orange-500/95 to-rose-500/95 backdrop-blur-xl border-b border-amber-400/30 px-3 py-2 flex items-center gap-3 shadow-xl shadow-amber-500/30 pt-safe"
      data-testid="smart-app-banner"
    >
      {/* App icon */}
      <img
        src="/icon-192.png"
        alt="X67"
        width={40}
        height={40}
        className="w-10 h-10 rounded-xl ring-2 ring-white/40 flex-shrink-0 shadow-md"
      />

      {/* App info */}
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold text-sm truncate leading-tight">X67 Digital</p>
        <p className="text-white/90 text-[11px] truncate leading-tight">
          Anunțuri • Licitații • Plată Sigură
        </p>
        <div className="flex items-center gap-1 mt-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className="w-2.5 h-2.5 fill-white text-white" />
          ))}
          <span className="text-white/90 text-[10px] ml-1 font-medium">Gratuit • Play Store</span>
        </div>
      </div>

      {/* Install button */}
      <button
        type="button"
        onClick={install}
        className="flex-shrink-0 px-4 py-2 rounded-lg bg-black hover:bg-zinc-800 text-white font-bold text-xs shadow-md active:scale-95 transition"
        data-testid="smart-banner-install-btn"
      >
        Instalează
      </button>

      {/* Dismiss */}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Închide"
        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/20 active:bg-white/30 transition text-white"
        data-testid="smart-banner-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
